import type { CabinetContext } from "@planerius/contracts/cabinet-auth"

import type { ApiProblem } from "../api/problem"
import type { SessionLostReason } from "../api/http"
import type { KnownPermission, PermissionSet } from "./permissions"
import { hasPermission } from "./permissions"

/**
 * Состояние доступа — то, что решает, ЧТО человек увидит на любом экране кабинета.
 *
 * ГЛАВНОЕ ПРАВИЛО ЭТОГО ФАЙЛА (ТЗ кабинета §4, ADR-012): «проверяем доступ» и «нет доступа» —
 * РАЗНЫЕ состояния, и первое никогда не превращается во второе само по себе. Пока профиль
 * не загружен, у кабинета нет оснований отказывать: человек с полными правами не должен
 * ни на долю секунды увидеть «нет доступа» и решить, что его уволили.
 *
 * Отсюда же четвёртое состояние — `failed`. Сорванный запрос профиля это НЕ «нет доступа»
 * и НЕ «не вошёл»: сессия цела, права неизвестны, показывать надо сбой и кнопку «Повторить».
 * Увести человека на экран входа при обрыве связи значит потребовать пароль там,
 * где достаточно было подождать.
 */

/** Профиль загружен: только в этом состоянии вообще можно судить о правах. */
export interface AccessReady {
  readonly kind: "ready"
  readonly context: CabinetContext
  readonly permissions: PermissionSet
}

export type AccessState =
  /** Идёт восстановление сессии или загрузка профиля. Внешне — обычный каркас загрузки. */
  | { readonly kind: "checking" }
  /** Сессии нет: экран входа. Причина нужна, чтобы отличить кражу от обычного протухания. */
  | { readonly kind: "anonymous"; readonly reason: AnonymousReason }
  /** Вошёл, но салон не выбран (409 tenant_not_selected) или недоступен: экран выбора салона. */
  | { readonly kind: "tenant_required"; readonly problem: ApiProblem | null }
  | AccessReady
  /** Профиль не загрузился по причине, не связанной с правами. */
  | { readonly kind: "failed"; readonly problem: ApiProblem }

/**
 * Почему человек не вошёл. Различать обязательно: после кражи refresh-токена мало увести
 * на вход — надо предложить сменить пароль, а после нажатия «выйти» предлагать нечего.
 */
export type AnonymousReason = SessionLostReason | "initial" | "signed_out"

export function isAccessReady(state: AccessState): state is AccessReady {
  return state.kind === "ready"
}

/**
 * Чем закончилась проверка раздела.
 *
 * `denied` возвращается ТОЛЬКО из состояния ready. Все остальные состояния дают
 * что угодно, кроме отказа: это и есть машинная формулировка правила из ТЗ.
 */
export type SectionAccess =
  | { readonly kind: "checking" }
  | { readonly kind: "allowed"; readonly access: AccessReady }
  | { readonly kind: "denied"; readonly access: AccessReady; readonly required: KnownPermission }
  | { readonly kind: "unauthenticated"; readonly reason: AnonymousReason }
  | { readonly kind: "tenant_required" }
  | { readonly kind: "failed"; readonly problem: ApiProblem }

/**
 * Пускать ли в раздел. `required === null` — раздел без права (например, поддержка):
 * он доступен любому вошедшему, но профиль дождаться всё равно надо, иначе оболочке
 * нечем нарисовать навигацию.
 */
export function evaluateSection(
  state: AccessState,
  required: KnownPermission | null,
): SectionAccess {
  switch (state.kind) {
    case "checking":
      return { kind: "checking" }
    case "anonymous":
      return { kind: "unauthenticated", reason: state.reason }
    case "tenant_required":
      return { kind: "tenant_required" }
    case "failed":
      return { kind: "failed", problem: state.problem }
    case "ready":
      if (required === null || hasPermission(state.permissions, required)) {
        return { kind: "allowed", access: state }
      }
      return { kind: "denied", access: state, required }
  }
}
